import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { type OrganizationMessageFieldsFragment } from "../../../generated/graphql";
import {
  audienceLabel,
  NO_LINK,
  reachSummary,
  toLocalInput,
} from "../message-content";
import useMessageComposer, { type When } from "../use-message-composer";
import AudienceField from "./audience-field";
import MessageFormFields from "./message-form-fields";
import MessagePreview from "./message-preview";

const styles = {
  layout: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "minmax(0, 3fr) minmax(0, 2fr)" },
    gap: 3,
    pt: 1,
  },
  step: { fontWeight: 600, mb: 1 },
  note: { mb: 2 },
  aside: { p: 2, alignSelf: "start", bgcolor: "grey.50" },
  companies: { flexWrap: "wrap", gap: 0.5 },
} satisfies Record<string, SxProps<Theme>>;

type MessageFormDialogProps = {
  readonly open: boolean;
  /** The message being edited, or `null` to write a new one. */
  readonly message: OrganizationMessageFieldsFragment | null;
  readonly onClose: () => void;
  readonly onSaved: (uuid: string) => void;
};

/**
 * Writes a message and, for a new message or a draft, sends it now or
 * schedules it in the same step. A scheduled or live message can only have
 * its content edited. The preview and reach update as staff type.
 */
const MessageFormDialog = ({
  open,
  message,
  onClose,
  onSaved,
}: MessageFormDialogProps) => {
  const composer = useMessageComposer({ open, message, onSaved });
  const { form, audience, reach, isNewOrDraft, saving } = composer;
  const title =
    message === null
      ? "New message"
      : isNewOrDraft
        ? "Edit draft"
        : "Edit message";

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={(_event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
    >
      {composer.confirmDialog}
      {composer.sendConfirmDialog}
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Box sx={styles.layout}>
          <Stack spacing={3}>
            {composer.error !== null && (
              <Alert severity="error">{composer.error}</Alert>
            )}
            <Box>
              <Typography sx={styles.step}>Who gets it</Typography>
              {isNewOrDraft ? (
                <AudienceField
                  audience={audience}
                  onChange={composer.setAudience}
                />
              ) : (
                <Stack direction="row" sx={styles.companies}>
                  {audience.allCompanies ? (
                    <Chip size="small" label="All companies" />
                  ) : (
                    audience.companies.map((company) => (
                      <Chip
                        key={company.uuid}
                        size="small"
                        label={company.name}
                      />
                    ))
                  )}
                </Stack>
              )}
            </Box>
            <Box>
              <Typography sx={styles.step}>What it says</Typography>
              {!isNewOrDraft && (
                <Alert severity="info" sx={styles.note}>
                  Edits show on every recipient&apos;s bell and banner at once.
                </Alert>
              )}
              <MessageFormFields form={form} onChange={composer.setForm} />
            </Box>
            {isNewOrDraft && (
              <Box>
                <Typography sx={styles.step}>When</Typography>
                <RadioGroup
                  row
                  value={composer.when}
                  onChange={(event) => {
                    composer.setWhen(event.target.value as When);
                  }}
                >
                  <FormControlLabel
                    value="NOW"
                    control={<Radio size="small" />}
                    label="Send now"
                  />
                  <FormControlLabel
                    value="LATER"
                    control={<Radio size="small" />}
                    label="Schedule"
                  />
                </RadioGroup>
                {composer.when === "LATER" && (
                  <TextField
                    type="datetime-local"
                    label="Starts at"
                    value={composer.startsAt}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: toLocalInput(new Date()) }}
                    helperText={`Your time zone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`}
                    onChange={(event) => {
                      composer.setStartsAt(event.target.value);
                    }}
                  />
                )}
              </Box>
            )}
          </Stack>
          <Paper variant="outlined" sx={styles.aside}>
            <Stack spacing={2}>
              <Typography sx={styles.step}>Preview</Typography>
              <MessagePreview
                severity={form.severity}
                title={form.title}
                body={form.body}
                linkLabel={form.linkType === NO_LINK ? null : form.linkLabel}
              />
              {isNewOrDraft &&
                (composer.audienceProblem === null &&
                composer.reachError !== undefined ? (
                  <Alert severity="warning" variant="outlined">
                    Could not count who it reaches, so sending is disabled:{" "}
                    {composer.reachError.message}
                  </Alert>
                ) : (
                  <Alert severity="info" variant="outlined">
                    {composer.audienceProblem ??
                      (reach === undefined
                        ? "Counting who it reaches..."
                        : `${audienceLabel(audience)}: ${reachSummary(form.severity, reach)}`)}
                  </Alert>
                ))}
            </Stack>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {isNewOrDraft ? (
          <>
            <Button
              variant="outlined"
              disabled={saving}
              onClick={() => {
                void composer.handleSave();
              }}
            >
              Save draft
            </Button>
            <Button
              variant="contained"
              disabled={saving || reach === undefined}
              onClick={() => {
                void composer.handleSend();
              }}
            >
              {composer.when === "NOW" ? "Send now" : "Schedule"}
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => {
              void composer.handleSave();
            }}
          >
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MessageFormDialog;
